"use client";

/**
 * BuyPhysicalButton — drop on any release / track page. Lists every pressing
 * attached to a release and, on click, opens a variant picker + Stripe
 * Checkout for `type: 'physical-media'`.
 *
 * Renders nothing when the release has no pressings. Logged-out fans see the
 * button but get redirected to /signin when they tap Buy.
 *
 * Variants pulled live from `pressings/{id}` so a creator's price / stock
 * change is reflected without a redeploy.
 */

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Disc3, Loader2, ShoppingBag, AlertCircle } from "lucide-react";
import { listPressings, FORMAT_LABELS, type Pressing, type PhysicalVariant } from "@/services/physical-media";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface BuyPhysicalButtonProps {
  releaseId: string;
  /** Hide the button entirely when no pressings exist (default true). */
  hideWhenEmpty?: boolean;
  className?: string;
}

export function BuyPhysicalButton({ releaseId, hideWhenEmpty = true, className }: BuyPhysicalButtonProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [pressings, setPressings] = useState<Pressing[]>([]);
  const [loading, setLoading]     = useState(true);
  const [open, setOpen]           = useState(false);
  const [buyingKey, setBuyingKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPressings({ releaseId })
      .then(ps => { if (!cancelled) setPressings(ps.filter(p => p.status === 'crowdfunding' || p.status === 'production' || p.status === 'shipping')); })
      .catch(() => { if (!cancelled) setPressings([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [releaseId]);

  if (loading) return null;
  if (pressings.length === 0 && hideWhenEmpty) return null;

  const handleBuy = async (pressing: Pressing, variant: PhysicalVariant | undefined) => {
    if (!user) {
      toast({ title: "Sign in required", description: "Sign in to order a physical copy.", variant: "destructive" });
      return;
    }
    try {
      const key = `${pressing.id}:${variant?.id || 'default'}`;
      setBuyingKey(key);
      const idToken = await user.getIdToken();
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          type: "physical-media",
          pressingId: pressing.id,
          variantId: variant?.id,
          quantity: 1,
          successUrl: `${window.location.origin}/music/${pressing.releaseId}?paid=1`,
          cancelUrl:  window.location.href,
        }),
      });
      if (!res.ok) throw new Error(`Checkout failed (HTTP ${res.status})`);
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e: any) {
      toast({ title: "Couldn't start checkout", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setBuyingKey(null);
    }
  };

  // Pick a primary format for the button label — "Buy on Vinyl" reads better
  // than "Buy Physical" when only one format exists.
  const primary = pressings[0];
  const onlyOneFormat = pressings.every(p => p.format === primary.format);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className={cn("gap-1.5", className)}
        onClick={() => setOpen(true)}
        data-help-title={onlyOneFormat ? `Order on ${FORMAT_LABELS[primary.format]}` : "Order physical copy"}
        data-help-description="Order a print-on-demand physical copy — vinyl, cassette, CD, DVD, or Blu-ray. Made to order through eco-friendly pressing plants. No inventory waste, ships across the EU + US."
      >
        <Disc3 className="h-3.5 w-3.5" />
        {onlyOneFormat ? `Buy on ${FORMAT_LABELS[primary.format]}` : "Buy physical"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" />
              Pick a format
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {pressings.map(p => (
              <div key={p.id} className="rounded-lg border border-border/60 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge>{FORMAT_LABELS[p.format]}</Badge>
                  <Badge variant="outline" className="capitalize">{p.status}</Badge>
                  {p.leadTimeWeeks ? <Badge variant="outline">Ships in ~{p.leadTimeWeeks}w</Badge> : null}
                </div>
                {p.status === "crowdfunding" && (p.minCopies || 0) > 0 && (
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                      <span><AlertCircle className="inline h-3 w-3 mr-1" />Crowdfunding</span>
                      <span>{p.currentCopies || 0} / {p.minCopies} sold</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${Math.min(100, ((p.currentCopies || 0) / Math.max(1, p.minCopies)) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  {(p.variants && p.variants.length > 0 ? p.variants : [undefined]).map(v => {
                    const priceCents = (v?.priceCents ?? p.basePriceCents) || 0;
                    const stockLeft = v?.stock ? Math.max(0, v.stock - (v.sold || 0)) : null;
                    const key = `${p.id}:${v?.id || 'default'}`;
                    return (
                      <Button
                        key={key}
                        size="sm"
                        variant="ghost"
                        className="w-full justify-between text-left h-auto py-2 px-3 border border-border/40 hover:border-primary/40"
                        disabled={!!buyingKey || (stockLeft !== null && stockLeft <= 0)}
                        onClick={() => handleBuy(p, v)}
                      >
                        <span className="flex flex-col items-start gap-0.5">
                          <span className="text-sm font-medium">
                            {v?.label || `${FORMAT_LABELS[p.format]} — standard`}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {[v?.colour, v?.weight, v?.sleeve, v?.size].filter(Boolean).join(" · ") || "Single copy, ships globally"}
                          </span>
                        </span>
                        <span className="flex flex-col items-end gap-0.5">
                          <span className="text-sm font-semibold">
                            {(p.currency || 'EUR')} {(priceCents / 100).toFixed(2)}
                          </span>
                          {stockLeft !== null && (
                            <span className="text-[10px] text-muted-foreground">
                              {stockLeft === 0 ? "Sold out" : `${stockLeft} left`}
                            </span>
                          )}
                          {buyingKey === key && <Loader2 className="h-3 w-3 animate-spin" />}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                {p.manufacturerNotes && (
                  <p className="text-[10px] text-muted-foreground">{p.manufacturerNotes}</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
